// src/pages/CapturePage/noiseMeter.js
//
// How loud the room is, measured rather than guessed.
//
// Three seconds of ambient audio with every bit of processing switched off —
// noise suppression would remove the very thing being measured — reduced to one
// RMS figure in dBFS. The examiner can still override the band it suggests:
// a person in the room hears the corridor door that a three-second sample missed.

export const RAW_AUDIO_CONSTRAINTS = {
    channelCount: 1,
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
};

export function bandFor(dbfs, bands) {
    // Bands are ordered quietest first; a louder room reads less negative.
    for (const band of bands || []) {
        if (band.max_dbfs === null || band.max_dbfs === undefined || dbfs <= band.max_dbfs) {
            return band.key;
        }
    }
    return 'noisy';
}

/**
 * Listen to the room. Resolves { dbfs, seconds } or throws.
 * onLevel(dbfs) is called roughly 10 times a second so a meter can move.
 */
export async function measureRoomNoise({ seconds = 3, onLevel } = {}) {
    let stream = null;
    let context = null;
    try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: RAW_AUDIO_CONSTRAINTS });
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) throw new Error('unsupported');
        context = new AudioCtx();
        const source = context.createMediaStreamSource(stream);
        const analyser = context.createAnalyser();
        analyser.fftSize = 2048;
        source.connect(analyser);

        const buffer = new Float32Array(analyser.fftSize);
        const squares = [];
        const started = Date.now();

        await new Promise((resolve) => {
            const tick = () => {
                analyser.getFloatTimeDomainData(buffer);
                let sum = 0;
                for (let i = 0; i < buffer.length; i += 1) sum += buffer[i] * buffer[i];
                const rms = Math.sqrt(sum / buffer.length);
                squares.push(rms * rms);
                if (onLevel) onLevel(toDbfs(rms));
                if (Date.now() - started >= seconds * 1000) resolve();
                else setTimeout(tick, 100);
            };
            tick();
        });

        const meanSquare = squares.reduce((a, b) => a + b, 0) / (squares.length || 1);
        return { dbfs: toDbfs(Math.sqrt(meanSquare)), seconds };
    } finally {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        if (context && context.state !== 'closed') context.close();
    }
}

function toDbfs(rms) {
    // Floor at -100: silence is log(0), and a number is easier to show than -Infinity.
    if (!rms || rms <= 0) return -100;
    return Math.max(-100, Math.round(20 * Math.log10(rms) * 10) / 10);
}
